/*eslint-disable*/
import React, { createContext, useState, useEffect, useContext } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import chatAPI from "../Services/Dashboard/ChatApi";
import { SOCKET_URL } from "../config";

// Create context
export const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");

  // Group chat state
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [groupTypingUsers, setGroupTypingUsers] = useState({});

  // Initialize Socket.IO connection when user is available
  useEffect(() => {
    if (!user) return;

    const socketInstance = io(SOCKET_URL, {
      auth: {
        token: localStorage.getItem("token"),
      },
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000,
      path: "/socket.io",
    });

    // Connection events
    socketInstance.on("connect", () => {
      setConnectionStatus("connected");
    });

    socketInstance.on("disconnect", () => {
      setConnectionStatus("disconnected");
    });

    socketInstance.on("connect_error", (error) => {
      setConnectionStatus("error");
    });

    socketInstance.on("reconnect_attempt", (attempt) => {
      setConnectionStatus("reconnecting");
    });

    socketInstance.on("reconnect", () => {
      setConnectionStatus("connected");
      // Reload active chat data on reconnection
      if (activeChat) {
        loadMessages(activeChat._id);
      }
      // Reload conversations
      loadConversations();
    });

    socketInstance.on("reconnect_failed", () => {
      setConnectionStatus("failed");
    });

    setSocket(socketInstance);

    // Cleanup on unmount
    return () => {
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };
  }, [user]);

  // Handle real-time events once socket is initialized
  useEffect(() => {
    if (!socket) return;

    // User online/offline status
    socket.on("user_status", ({ userId, status }) => {
      setOnlineUsers((prev) => ({ ...prev, [userId]: status === "online" }));
    });

    // Receiving a message
    socket.on("receive_message", (message) => {
      // Update messages if from active chat
      if (
        activeChat &&
        (message.sender._id === activeChat._id ||
          message.receiver === activeChat._id)
      ) {
        setMessages((prev) => [...prev, message]);

        // Mark as read if this is the active chat
        socket.emit("mark_read", { messageIds: [message._id] });
      }

      // Update unread counts
      if (!activeChat || message.sender._id !== activeChat._id) {
        setUnreadCounts((prev) => ({
          ...prev,
          [message.sender._id]: (prev[message.sender._id] || 0) + 1,
        }));
      }

      // Update conversations list
      updateConversationWithMessage(message);
    });

    // Message sent confirmation
    socket.on("message_sent", (message) => {
      // Add to messages if related to active chat
      if (activeChat && message.receiver === activeChat._id) {
        setMessages((prev) => [...prev, message]);
      }

      // Update conversations list
      updateConversationWithMessage(message);
    });

    // Typing indicator
    socket.on("user_typing", ({ userId }) => {
      setTypingUsers((prev) => ({ ...prev, [userId]: true }));

      // Clear typing indicator after a delay
      setTimeout(() => {
        setTypingUsers((prev) => ({ ...prev, [userId]: false }));
      }, 3000);
    });

    // Messages read receipts
    socket.on("messages_read", ({ messageIds }) => {
      setMessages((prev) =>
        prev.map((msg) =>
          messageIds.includes(msg._id) ? { ...msg, read: true } : msg
        )
      );
    });

    // ── Group events ──
    socket.on("receive_group_message", (message) => {
      // Update group messages if this group is active
      if (activeGroup && message.groupId === activeGroup._id) {
        setGroupMessages((prev) => [...prev, message]);
      }

      // Update groups list with latest message
      setGroups((prev) =>
        prev.map((g) =>
          g._id === message.groupId
            ? {
                ...g,
                lastMessage: {
                  text: message.message,
                  sender: message.sender,
                  createdAt: message.createdAt,
                },
              }
            : g
        )
      );
    });

    socket.on("group_user_typing", ({ groupId, userId: typingUserId }) => {
      if (activeGroup && groupId === activeGroup._id) {
        setGroupTypingUsers((prev) => ({ ...prev, [typingUserId]: true }));
        setTimeout(() => {
          setGroupTypingUsers((prev) => ({ ...prev, [typingUserId]: false }));
        }, 3000);
      }
    });

    // Socket event cleanup
    return () => {
      socket.off("user_status");
      socket.off("receive_message");
      socket.off("message_sent");
      socket.off("user_typing");
      socket.off("messages_read");
      socket.off("receive_group_message");
      socket.off("group_user_typing");
    };
  }, [socket, activeChat, activeGroup]);

  // Update conversations list with a new message
  const updateConversationWithMessage = (message) => {
    setConversations((prev) => {
      // Find if conversation already exists
      const existingIndex = prev.findIndex(
        (conv) =>
          conv.user._id === message.sender._id ||
          conv.user._id === message.receiver
      );

      // Get the other user (not current user)
      const otherUserId =
        message.sender._id === user.id ? message.receiver : message.sender._id;
      const otherUser =
        message.sender._id === user.id
          ? {
              _id: message.receiver,
              username: prev[existingIndex]?.user.username || "User",
            }
          : { _id: message.sender._id, username: message.sender.username };

      if (existingIndex >= 0) {
        // Update existing conversation
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          lastMessage: {
            message: message.message,
            createdAt: message.createdAt,
            fromMe: message.sender._id === user.id,
          },
        };

        // Move this conversation to the top
        const [conversation] = updated.splice(existingIndex, 1);
        return [conversation, ...updated];
      } else {
        // Create new conversation
        return [
          {
            user: otherUser,
            unreadCount: message.sender._id === user.id ? 0 : 1,
            lastMessage: {
              message: message.message,
              createdAt: message.createdAt,
              fromMe: message.sender._id === user.id,
            },
          },
          ...prev,
        ];
      }
    });
  };

  // Load conversations function - extracted for reuse
  const loadConversations = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const response = await chatAPI.getRecentChats();

      if (response.success) {
        setConversations(response.data);

        // Initialize unread counts
        const counts = {};
        response.data.forEach((conv) => {
          if (conv.unreadCount > 0) {
            counts[conv.user._id] = conv.unreadCount;
          }
        });
        setUnreadCounts(counts);
      } else {
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  // Load messages function - extracted for reuse
  const loadMessages = async (userId) => {
    if (!userId) return;

    try {
      setMessagesLoading(true);
      const response = await chatAPI.getChatMessages(userId);

      if (response.success) {
        setMessages(response.data);

        // Reset unread count for this conversation
        setUnreadCounts((prev) => ({ ...prev, [userId]: 0 }));
      } else {
      }

      setMessagesLoading(false);
    } catch (error) {
      setMessagesLoading(false);
    }
  };

  // ── Group functions ──
  const loadGroups = async () => {
    if (!user) return;
    try {
      const res = await chatAPI.listGroups();
      if (res.success) setGroups(res.data);
    } catch {}
  };

  const loadGroupMessages = async (groupId) => {
    if (!groupId) return;
    try {
      const res = await chatAPI.getGroupMessages(groupId);
      if (res.success) setGroupMessages(res.data);
    } catch {}
  };

  const sendGroupMessage = (text, attachment) => {
    if (!socket || !activeGroup) return;
    const payload = { groupId: activeGroup._id, message: text };
    if (attachment) payload.attachment = attachment;
    socket.emit("send_group_message", payload);
  };

  const sendGroupTyping = () => {
    if (!socket || !activeGroup) return;
    socket.emit("group_typing", { groupId: activeGroup._id });
  };

  // When switching to a group, clear DM active chat and vice versa
  const switchToGroup = (group) => {
    setActiveChat(null);
    setActiveGroup(group);
  };

  const switchToChat = (chatUser) => {
    setActiveGroup(null);
    setGroupMessages([]);
    setActiveChat(chatUser);
  };

  // Load conversations when component mounts
  useEffect(() => {
    loadConversations();
    loadGroups();
  }, [user]);

  // Load messages for active chat
  useEffect(() => {
    if (!activeChat || !user) return;
    loadMessages(activeChat._id);
  }, [activeChat, user]);

  // Load messages for active group
  useEffect(() => {
    if (!activeGroup || !user) return;
    loadGroupMessages(activeGroup._id);
  }, [activeGroup, user]);

  // Send a message (optionally with attachment metadata)
  const sendMessage = (text, attachment) => {
    if (!socket || !activeChat) return;

    const payload = {
      receiverId: activeChat._id,
      message: text,
    };
    if (attachment) payload.attachment = attachment;

    socket.emit("send_message", payload);
  };

  // Indicate user is typing
  const sendTypingIndicator = () => {
    if (!socket || !activeChat) return;

    socket.emit("typing", {
      receiverId: activeChat._id,
    });
  };

  // Mark messages as read
  const markMessagesAsRead = (messageIds) => {
    if (!socket || messageIds.length === 0) return;

    socket.emit("mark_read", { messageIds });
  };

  return (
    <ChatContext.Provider
      value={{
        conversations,
        activeChat,
        setActiveChat: switchToChat,
        messages,
        onlineUsers,
        typingUsers,
        unreadCounts,
        isLoading,
        messagesLoading,
        connectionStatus,
        sendMessage,
        sendTypingIndicator,
        markMessagesAsRead,
        // Group
        groups,
        activeGroup,
        setActiveGroup: switchToGroup,
        groupMessages,
        groupTypingUsers,
        sendGroupMessage,
        sendGroupTyping,
        loadGroups,
        socket,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

// Custom hook for using the chat context
export const useChat = () => useContext(ChatContext);
